if (!customElements.get('shop-the-look')) {
  // Keeps each curated Look generic while reflecting live customer choices.
  customElements.define(
    'shop-the-look',
    class ShopTheLook extends HTMLElement {
      constructor() {
        super();
        this.slots = Array.from(this.querySelectorAll('[data-look-slot]'));
        this.totalElement = this.querySelector('[data-look-total]');
        this.addButton = this.querySelector('[data-add-look]');
        this.errorElement = this.querySelector('[data-look-error]');
        this.liveRegion = this.querySelector('[data-look-live-region]');
        this.toast = this.querySelector('[data-look-toast]');
        this.carousels = Array.from(this.querySelectorAll('[data-choice-carousel]'));
        this.toastTimer = null;
        this.carouselFrames = new WeakMap();
        this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'BGN';
        this.locale = document.documentElement.lang || 'bg-BG';
        this.moneyFormatters = new Map();

        this.bindEvents();
        this.slots.forEach((slot) => this.updateSlot(slot));
        this.updateAll();
        window.requestAnimationFrame(() => {
          this.carousels.forEach((carousel) => this.updateCarouselState(carousel));
        });
      }

      bindEvents() {
        this.querySelectorAll('[data-variant-choice]').forEach((choice) => {
          choice.addEventListener('change', () => {
            const slot = choice.closest('[data-look-slot]');
            if (slot) {
              this.updateSlot(slot);
              this.updateChoiceStates(slot);
            }
            this.updateAll();
            this.showToast('Добавено към композицията');
          });
        });

        this.querySelectorAll('[data-slot-toggle]').forEach((toggle) => {
          toggle.addEventListener('change', () => {
            const slot = toggle.closest('[data-look-slot]');
            if (!slot) return;
            const wasSelected = slot.dataset.selected !== 'false';
            slot.dataset.selected = String(toggle.checked);
            toggle.nextElementSibling.textContent = toggle.checked
              ? 'Премахнете от разказа'
              : 'Добавете към разказа';
            this.updateSlotActionLabels(slot, toggle.checked);
            this.updateAll();
            if (wasSelected !== toggle.checked) {
              this.showToast(
                toggle.checked ? 'Добавено обратно към композицията' : 'Премахнато от композицията'
              );
            }
          });
        });

        this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
          thumb.addEventListener('click', () => this.selectGalleryImage(thumb));
        });

        this.querySelectorAll('[data-summary-toggle]').forEach((button) => {
          button.addEventListener('click', () => {
            const line = button.closest('[data-summary-line]');
            const slot = this.slots.find((item) => item.dataset.slotHandle === line?.dataset.summaryLine);
            const toggle = slot?.querySelector('[data-slot-toggle]');
            if (!toggle) return;
            toggle.checked = !toggle.checked;
            toggle.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        this.querySelectorAll('[data-slot-action]').forEach((button) => {
          button.addEventListener('click', () => {
            const slot = button.closest('[data-look-slot]');
            const toggle = slot?.querySelector('[data-slot-toggle]');
            if (!toggle) return;
            toggle.checked = !toggle.checked;
            toggle.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        this.querySelectorAll('[data-carousel-prev], [data-carousel-next]').forEach((button) => {
          button.addEventListener('click', () => {
            const choices = button.closest('.look-slot__choices');
            const carousel = choices?.querySelector('[data-choice-carousel]');
            if (!carousel) return;
            this.scrollCarousel(carousel, button.hasAttribute('data-carousel-next') ? 1 : -1);
          });
        });

        this.carousels.forEach((carousel) => {
          carousel.addEventListener(
            'scroll',
            () => {
              if (this.carouselFrames.has(carousel)) return;
              const frame = window.requestAnimationFrame(() => {
                this.carouselFrames.delete(carousel);
                this.updateCarouselState(carousel);
              });
              this.carouselFrames.set(carousel, frame);
            },
            { passive: true }
          );
        });
        window.addEventListener('resize', () => {
          this.carousels.forEach((carousel) => this.updateCarouselState(carousel));
        });

        this.addButton?.addEventListener('click', () => this.addLookToCart());
      }

      getSelectedChoice(slot) {
        return (
          slot.querySelector('[data-variant-choice]:checked') ||
          slot.querySelector('input[type="hidden"][data-variant-choice]')
        );
      }

      updateSlot(slot) {
        const choice = this.getSelectedChoice(slot);
        if (!choice) return;

        const productTitle = slot.querySelector('[data-selected-product-title]');
        const variantTitle = slot.querySelector('[data-selected-variant-title]');
        const price = slot.querySelector('[data-selected-price]');
        const availability = slot.querySelector('[data-selected-availability]');
        const available = choice.dataset.available === 'true';

        slot.querySelectorAll('[data-variant-gallery]').forEach((gallery) => {
          const isSelected = gallery.dataset.variantGallery === choice.dataset.variantId;
          gallery.hidden = !isSelected;
          if (isSelected) this.resetGallery(gallery);
        });

        if (productTitle) productTitle.textContent = choice.dataset.productTitle || '';
        if (variantTitle) variantTitle.textContent = choice.dataset.variantTitle || '';
        if (price) {
          price.textContent = choice.dataset.priceFormatted || this.formatMoney(Number(choice.dataset.price));
        }

        if (availability) {
          availability.textContent = available ? 'В наличност' : 'Временно изчерпан';
          availability.classList.toggle('look-slot__availability--out', !available);
        }
      }

      updateChoiceStates(slot) {
        slot.querySelectorAll('.look-choice').forEach((card) => {
          const input = card.querySelector('[data-variant-choice]');
          card.setAttribute('aria-selected', input?.checked ? 'true' : 'false');
        });
      }

      updateSlotActionLabels(slot, included) {
        slot.querySelectorAll('[data-slot-action]').forEach((button) => {
          button.textContent = included ? 'Премахнете от разказа' : 'Добавете към разказа';
        });
      }

      scrollCarousel(carousel, direction) {
        const card = carousel.querySelector('.look-choice');
        const styles = window.getComputedStyle(carousel);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const distance = (card?.getBoundingClientRect().width || carousel.clientWidth * 0.8) + gap;
        carousel.scrollLeft = Math.max(0, carousel.scrollLeft + distance * direction);
        window.setTimeout(() => this.updateCarouselState(carousel), 350);
      }

      updateCarouselState(carousel) {
        const choices = carousel.closest('.look-slot__choices');
        const controls = choices?.querySelector('[data-carousel-controls]');
        if (!controls) return;

        const canScroll = carousel.scrollWidth - carousel.clientWidth > 2;
        const previous = controls.querySelector('[data-carousel-prev]');
        const next = controls.querySelector('[data-carousel-next]');
        controls.hidden = !canScroll;
        if (previous) previous.disabled = !canScroll || carousel.scrollLeft <= 2;
        if (next) {
          next.disabled =
            !canScroll || carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;
        }
      }

      showToast(message) {
        if (!this.toast || !message) return;
        window.clearTimeout(this.toastTimer);
        this.toast.textContent = message;
        this.toast.hidden = false;
        this.toastTimer = window.setTimeout(() => {
          this.toast.hidden = true;
        }, 2800);
      }

      resetGallery(gallery) {
        gallery.querySelectorAll('[data-gallery-slide]').forEach((slide, index) => {
          slide.hidden = index !== 0;
        });
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb, index) => {
          thumb.classList.toggle('is-active', index === 0);
          thumb.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        });
      }

      selectGalleryImage(thumb) {
        const gallery = thumb.closest('[data-variant-gallery]');
        if (!gallery) return;
        const selectedIndex = thumb.dataset.galleryThumb;

        gallery.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
          slide.hidden = slide.dataset.gallerySlide !== selectedIndex;
        });
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => {
          const isSelected = item === thumb;
          item.classList.toggle('is-active', isSelected);
          item.setAttribute('aria-current', isSelected ? 'true' : 'false');
        });
      }

      getSelection() {
        return this.slots
          .filter((slot) => slot.dataset.selected !== 'false')
          .map((slot) => {
            const choice = this.getSelectedChoice(slot);
            return {
              slot,
              choice,
              id: Number(choice?.dataset.variantId),
              price: Number(choice?.dataset.price),
              quantity: Math.max(1, Number(slot.dataset.quantity) || 1),
              available: choice?.dataset.available === 'true',
              optional: slot.dataset.slotType === 'optional',
            };
          });
      }

      validateSelection(selection = this.getSelection()) {
        const invalid = selection.filter((item) => !item.id || !item.available);

        return {
          valid: invalid.length === 0 && selection.length > 0,
          invalid,
        };
      }

      updateAll() {
        const selection = this.getSelection();
        const total = selection.reduce((sum, item) => {
          if (!item.available || !Number.isFinite(item.price)) return sum;
          return sum + item.price * item.quantity;
        }, 0);
        const validation = this.validateSelection(selection);

        this.slots.forEach((slot) => this.updateSummaryLine(slot));
        this.slots.forEach((slot) => this.updateOverviewItem(slot));
        if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);
        this.updateFreeShipping(total);
        if (this.addButton && !this.addButton.classList.contains('loading')) {
          this.addButton.disabled = !validation.valid;
        }
        if (selection.length === 0) {
          this.showError('Добавете поне един предмет към разказа, за да продължите.');
        } else {
          this.clearError();
        }
      }

      updateSummaryLine(slot) {
        const summaryLine = this.querySelector(
          `[data-summary-line="${CSS.escape(slot.dataset.slotHandle || '')}"]`
        );
        if (!summaryLine) return;

        const choice = this.getSelectedChoice(slot);
        const selected = slot.dataset.selected !== 'false';
        const quantity = Math.max(1, Number(slot.dataset.quantity) || 1);
        const variant = summaryLine.querySelector('[data-summary-variant]');
        const price = summaryLine.querySelector('[data-summary-price]');
        const toggle = summaryLine.querySelector('[data-summary-toggle]');
        const thumbnail = summaryLine.querySelector('[data-summary-thumbnail]');
        const image = summaryLine.querySelector('[data-summary-image]');
        const detail = [choice?.dataset.productTitle, choice?.dataset.variantTitle]
          .filter((value, index, values) => value && (index === 0 || value !== 'Default Title') && values.indexOf(value) === index)
          .join(' - ');

        summaryLine.classList.toggle('is-removed', !selected);
        if (variant) {
          variant.textContent = selected ? `${detail}${quantity > 1 ? ` × ${quantity}` : ''}` : 'Не е включен';
        }
        if (price) {
          price.textContent =
            selected && choice?.dataset.available === 'true'
              ? this.formatMoney(Number(choice.dataset.price) * quantity)
              : '-';
        }
        if (thumbnail && image) {
          const imageUrl = choice?.dataset.imageUrl;
          thumbnail.hidden = !imageUrl;
          if (imageUrl) {
            image.src = imageUrl;
            image.alt = choice?.dataset.imageAlt || choice?.dataset.productTitle || '';
          }
        }
        if (toggle) {
          toggle.hidden = !slot.querySelector('[data-slot-toggle]');
          toggle.textContent = selected ? 'Премахнете от разказа' : 'Добавете към разказа';
        }
      }

      updateOverviewItem(slot) {
        const overviewItem = this.querySelector(
          `[data-overview-item="${CSS.escape(slot.dataset.slotHandle || '')}"]`
        );
        if (!overviewItem) return;

        const selected = slot.dataset.selected !== 'false';
        const state = overviewItem.querySelector('[data-overview-state]');
        const mark = overviewItem.querySelector('[data-overview-mark]');
        const optional = overviewItem.dataset.overviewOptional === 'true';
        overviewItem.classList.toggle('is-removed', !selected);
        if (mark) mark.textContent = selected ? (optional ? '○' : '✓') : '○';
        if (state) {
          state.hidden = selected && !optional;
          state.textContent = selected
            ? 'по избор - включено'
            : optional
              ? 'по избор - не е включено'
              : 'не е включено';
        }
      }

      updateFreeShipping(totalCents) {
        const message = this.querySelector('[data-free-shipping]');
        if (!message) return;

        const threshold = this.currency === 'EUR' ? 10000 : this.currency === 'BGN' ? 19558 : null;
        message.hidden = threshold === null || totalCents <= threshold;
      }

      getMoneyFormatter(currency) {
        if (!this.moneyFormatters.has(currency)) {
          this.moneyFormatters.set(
            currency,
            new Intl.NumberFormat(this.locale, {
              style: 'currency',
              currency,
              minimumFractionDigits: 2,
            })
          );
        }
        return this.moneyFormatters.get(currency);
      }

      formatMoney(cents) {
        const amount = (Number(cents) || 0) / 100;
        if (this.currency === 'EUR') {
          return `${this.getMoneyFormatter('EUR').format(amount)} (${this.getMoneyFormatter('BGN').format(
            amount * 1.95583
          )})`;
        }
        if (this.currency === 'BGN') {
          return `${this.getMoneyFormatter('BGN').format(amount)} (${this.getMoneyFormatter('EUR').format(
            amount / 1.95583
          )})`;
        }
        return this.getMoneyFormatter(this.currency).format(amount);
      }

      async addLookToCart() {
        const selection = this.getSelection();
        const validation = this.validateSelection(selection);

        if (!validation.valid) {
          this.showError(
            selection.length === 0
              ? 'Добавете поне един предмет към разказа, за да продължите.'
              : 'Моля, прегледайте избраните артикули.'
          );
          return;
        }

        const instanceId =
          window.crypto && typeof window.crypto.randomUUID === 'function'
            ? window.crypto.randomUUID()
            : `look-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        const payload = {
          items: selection.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            properties: {
              _look_handle: this.dataset.lookHandle,
              _look_instance_id: instanceId,
              _look_slot: item.slot.dataset.slotHandle,
            },
          })),
          sections: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        };

        this.setLoading(true);
        this.clearError();

        try {
          const response = await fetch(window.routes?.cart_add_url || `${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Артикулите не можаха да бъдат добавени.');
          }

          await this.refreshCartNotification(result);

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'shop-the-look',
              cartData: result,
            });
          }

          if (this.liveRegion) {
            this.liveRegion.textContent = 'Композицията беше добавена в количката.';
          }
        } catch (error) {
          console.error(error);
          this.showError(error?.message || 'Възникна проблем при добавянето. Моля, опитайте отново.');
        } finally {
          this.setLoading(false);
        }
      }

      async refreshCartNotification(result) {
        const cartNotification = document.querySelector('cart-notification');
        const sections = result.sections || {};

        this.replaceSection('cart-icon-bubble', sections['cart-icon-bubble']);
        this.replaceSection('cart-notification-button', sections['cart-notification-button']);

        if (!cartNotification || !sections['cart-notification-product']) {
          window.location.assign(window.routes?.cart_url || `${window.Shopify.routes.root}cart`);
          return;
        }

        const parsed = new DOMParser().parseFromString(sections['cart-notification-product'], 'text/html');
        const notificationProducts = document.getElementById('cart-notification-product');

        if (notificationProducts) {
          notificationProducts.innerHTML = '';
          notificationProducts.classList.toggle('look-cart-notification-products', (result.items || []).length > 1);
          (result.items || []).forEach((item) => {
            const itemKey = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(item.key) : item.key;
            const addedItem = parsed.querySelector(`[id="cart-notification-product-${itemKey}"]`);
            if (addedItem) notificationProducts.appendChild(addedItem.cloneNode(true));
          });
        }

        if (cartNotification.header) cartNotification.header.reveal();
        cartNotification.setActiveElement?.(this.addButton);
        cartNotification.open();
      }

      replaceSection(id, html) {
        const target = document.getElementById(id);
        if (!target || !html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('.shopify-section');
        target.innerHTML = section ? section.innerHTML : html;
      }

      setLoading(loading) {
        if (!this.addButton) return;
        this.addButton.classList.toggle('loading', loading);
        this.addButton.setAttribute('aria-disabled', String(loading));
        this.addButton.disabled = loading;
        this.addButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (!loading) this.updateAll();
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      clearError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
    }
  );
}
